import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiPlus, FiEdit2, FiTrash2, FiEye, FiFilter, FiX, 
  FiTrendingUp, FiTrendingDown, FiMinus, FiActivity 
} from 'react-icons/fi';
import qualityMetricService from '../../services/qualityMetricService';
import projectService from '../../services/projectService';
import CommonModal from '../../services/CommonModal';

const QualityMetrics = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
    const confirmDelete = () => {
    handleDelete(selectedMetric);
    setShowDeleteModal(false);
    setSelectedMetric(null);
  };
  const [qualityMetrics, setQualityMetrics] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    project_id: searchParams.get('project_id') || '',
    metric_type: searchParams.get('metric_type') || '',
    category: searchParams.get('category') || '',
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    trend: searchParams.get('trend') || '',
    measurement_period: searchParams.get('measurement_period') || '',
    is_active: searchParams.get('is_active') || 'true'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const metricTypes = [
    'Defect Density', 'Code Quality', 'Test Coverage', 'Customer Satisfaction',
    'Performance', 'Security', 'Maintainability', 'Reliability',
    'Usability', 'Compliance', 'Documentation Quality', 'Code Review',
    'Technical Debt', 'Other'
  ];

  const statuses = ['On Track', 'At Risk', 'Off Track', 'Excellent', 'Not Measured'];
  const trends = ['Improving', 'Stable', 'Declining', 'N/A'];
  const categories = ['Process', 'Product', 'Project', 'Resource', 'Customer'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  const measurementPeriods = ['Daily', 'Weekly', 'Bi-Weekly', 'Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];

  useEffect(() => {
    fetchQualityMetrics();
    fetchProjects();
    fetchStats();
  }, [filters, pagination.page]);

  const fetchQualityMetrics = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const data = await qualityMetricService.getQualityMetrics(params);
      setQualityMetrics(data.qualityMetrics || []);
       setQualityMetrics(data.data || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to fetch quality metrics');
      console.error('Error fetching quality metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 1000 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      const reponse = await qualityMetricService.getQualityMetricStats(params);
        const data = reponse.data;
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(name, value);
    } else {
      newParams.delete(name);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      project_id: '',
      metric_type: '',
      category: '',
      status: '',
      priority: '',
      trend: '',
      measurement_period: '',
      is_active: 'true'
    });
    setSearchParams({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {
   

    try {
      await qualityMetricService.deleteQualityMetric(id);
      toast.success('Quality metric deleted successfully');
      fetchQualityMetrics();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete quality metric');
      console.error('Error deleting quality metric:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Excellent': 'bg-green-100 text-green-800',
      'On Track': 'bg-blue-100 text-blue-800',
      'At Risk': 'bg-yellow-100 text-yellow-800',
      'Off Track': 'bg-red-100 text-red-800',
      'Not Measured': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'bg-red-100 text-red-800',
      'High': 'bg-orange-100 text-orange-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'Improving':
        return <FiTrendingUp className="text-green-600" />;
      case 'Declining':
        return <FiTrendingDown className="text-red-600" />;
      case 'Stable':
        return <FiMinus className="text-blue-600" />;
      default:
        return <FiActivity className="text-gray-600" />;
    }
  };

  const calculateAchievement = (metric) => {
    if (!metric.target_value || metric.target_value === 0) return 'N/A';
    const percentage = ((metric.actual_value / metric.target_value) * 100).toFixed(1);
    return `${percentage}%`;
  };

  if (loading && qualityMetrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading quality metrics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quality Metrics</h1>
          <p className="text-gray-600 mt-1">Track and monitor project quality metrics</p>
        </div>
        <Link
          to="/app/quality-metrics/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> Add Quality Metric
        </Link>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Metrics</div>
            <div className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">On Track</div>
            <div className="text-3xl font-bold text-blue-600 mt-2">
              {stats.byStatus?.['On Track'] || 0}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">At Risk</div>
            <div className="text-3xl font-bold text-yellow-600 mt-2">
              {stats.byStatus?.['At Risk'] || 0}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Avg Achievement</div>
            <div className="text-3xl font-bold text-green-600 mt-2">
              {stats.averageAchievement ? `${stats.averageAchievement.toFixed(1)}%` : 'N/A'}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <FiFilter /> {showFilters ? 'Hide' : 'Show'} Filters
          </button>
          {Object.values(filters).some(v => v && v !== 'true') && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <FiX /> Clear Filters
            </button>
          )}
        </div>
                          <CommonModal
        isOpen={showDeleteModal}
        title="Delete Metric"
        message="Are you sure you want to delete this metric?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedMetric(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <input
              type="text"
              name="search"
              placeholder="Search metrics..."
              value={filters.search}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            />
            <select
              name="project_id"
              value={filters.project_id}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <select
              name="metric_type"
              value={filters.metric_type}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Types</option>
              {metricTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Statuses</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <select
              name="priority"
              value={filters.priority}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Priorities</option>
              {priorities.map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
            <select
              name="trend"
              value={filters.trend}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Trends</option>
              {trends.map(trend => (
                <option key={trend} value={trend}>{trend}</option>
              ))}
            </select>
            <select
              name="measurement_period"
              value={filters.measurement_period}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Periods</option>
              {measurementPeriods.map(period => (
                <option key={period} value={period}>{period}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Quality Metrics Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metric Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target / Actual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Achievement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {qualityMetrics.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    No quality metrics found
                  </td>
                </tr>
              ) : (
                qualityMetrics.map((metric) => (
                  <tr key={metric.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{metric.metric_name}</div>
                      <div className="text-sm text-gray-500">{metric.project?.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{metric.metric_type}</div>
                      <div className="text-sm text-gray-500">{metric.category}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {metric.target_value} {metric.measurement_unit}
                      </div>
                      <div className="text-sm text-gray-500">
                        {metric.actual_value} {metric.measurement_unit}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {calculateAchievement(metric)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(metric.status)}`}>
                        {metric.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTrendIcon(metric.trend)}
                        <span className="text-sm text-gray-900">{metric.trend}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(metric.priority)}`}>
                        {metric.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/app/quality-metrics/${metric.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => navigate(`/app/quality-metrics/${metric.id}/edit`)}
                          className="text-green-600 hover:text-green-900"
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedMetric(metric.id);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                  <span className="font-medium">{pagination.pages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QualityMetrics;

